import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import apiService from '../../../services/api';

/**
 * 파일 선택 + 파일별 프롬프트 선택 컴포넌트
 * 각 파일마다 개별적으로 프롬프트를 선택할 수 있습니다.
 */
const FileSelectorWithPrompts = ({
  files = [],
  filePromptMapping = {},  // { fileId: promptId }
  onMappingChange,
  disabled = false,
  loading = false,
  selectedTenant
}) => {
  // 각 파일별 프롬프트 목록을 저장
  const [filePrompts, setFilePrompts] = useState({});  // { fileId: [prompts] }
  const [loadingPrompts, setLoadingPrompts] = useState({});  // { fileId: boolean }

  // 파일이 선택되었는지 확인
  const isFileSelected = (fileId) => {
    return filePromptMapping.hasOwnProperty(fileId);
  };

  // 선택된 파일 개수
  const selectedFilesCount = Object.keys(filePromptMapping).length;

  // 파일 선택/해제
  const handleToggleFile = async (fileId, checked) => {
    if (checked) {
      // 파일 선택: 프롬프트 로드만 하고, 자동 선택 안 함
      await loadPromptsForFile(fileId);

      console.log(`[FileSelectorWithPrompts] 파일 선택: ${fileId} (프롬프트 수동 선택 필요)`);

      // 빈 배열로 시작 - 사용자가 직접 프롬프트 선택
      onMappingChange({
        ...filePromptMapping,
        [fileId]: []  // 빈 배열 = 사용자가 선택해야 함
      });
    } else {
      // 파일 해제
      const newMapping = { ...filePromptMapping };
      delete newMapping[fileId];
      onMappingChange(newMapping);
    }
  };

  // 파일별 프롬프트 변경 (멀티 셀렉트)
  const handlePromptChange = (fileId, selectedPromptIds) => {
    console.log(`[FileSelectorWithPrompts] Prompt 변경: File=${fileId}, Selected=`, selectedPromptIds);
    console.log(`[FileSelectorWithPrompts] Type check:`, selectedPromptIds.map(id => `"${id}" (${typeof id})`));
    onMappingChange({
      ...filePromptMapping,
      [fileId]: selectedPromptIds // 배열
    });
  };

  // 파일의 프롬프트 목록 로드 (프롬프트 배열 반환)
  const loadPromptsForFile = async (fileId) => {
    console.log('[FileSelectorWithPrompts] loadPromptsForFile called for:', fileId);

    if (filePrompts[fileId]) {
      console.log('[FileSelectorWithPrompts] Prompts already loaded for:', fileId);
      return filePrompts[fileId]; // 이미 로드된 프롬프트 반환
    }

    setLoadingPrompts(prev => ({ ...prev, [fileId]: true }));

    try {
      console.log('[FileSelectorWithPrompts] Calling API for tenant:', selectedTenant, 'file:', fileId);
      const response = await apiService.getFilePrompts(selectedTenant, fileId);
      console.log('[FileSelectorWithPrompts] API response:', response);

      if (response.success && response.data && response.data.length > 0) {
        const prompts = response.data.map(p => ({
          prompt_id: p.prompt_id,
          prompt_name: p.prompt_name || p.prompt_id
        }));
        console.log('[FileSelectorWithPrompts] Processed prompts:', prompts);
        setFilePrompts(prev => ({ ...prev, [fileId]: prompts }));
        return prompts;  // 로드된 프롬프트 반환
      } else {
        console.warn('[FileSelectorWithPrompts] No prompts found for file:', fileId);
        setFilePrompts(prev => ({ ...prev, [fileId]: [] }));
        return [];
      }
    } catch (error) {
      console.error(`[FileSelectorWithPrompts] Failed to load prompts for ${fileId}:`, error);
      setFilePrompts(prev => ({ ...prev, [fileId]: [] }));
      return [];
    } finally {
      setLoadingPrompts(prev => ({ ...prev, [fileId]: false }));
    }
  };

  // 전체 선택
  const handleSelectAll = async () => {
    const newMapping = {};

    // 모든 파일의 프롬프트 로드 (병렬) - 각 파일별 프롬프트 배열 반환
    const promptResults = await Promise.all(
      files.map(async (file) => ({
        fileId: file.file_id,
        prompts: await loadPromptsForFile(file.file_id)
      }))
    );

    // 각 파일에 모든 프롬프트 자동 선택
    for (const { fileId, prompts } of promptResults) {
      const allPromptIds = prompts.map(p => p.prompt_id);
      newMapping[fileId] = allPromptIds;
      console.log(`[FileSelectorWithPrompts] 전체 선택 - ${fileId}: ${allPromptIds.length}개 프롬프트`);
    }

    onMappingChange(newMapping);
  };

  // 전체 해제
  const handleDeselectAll = () => {
    onMappingChange({});
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          파일 선택 및 프롬프트 설정
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAll}
            disabled={disabled || loading || files.length === 0}
          >
            전체 선택
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleDeselectAll}
            disabled={disabled || loading || selectedFilesCount === 0}
          >
            전체 해제
          </Button>
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          maxHeight: 500,
          overflow: 'auto',
          bgcolor: disabled || loading ? 'action.disabledBackground' : 'background.paper'
        }}
      >
        {files.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {loading ? '파일 목록을 불러오는 중...' : '사용 가능한 파일이 없습니다'}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ p: 1 }}>
            {files.map((file) => {
              const selected = isFileSelected(file.file_id);
              const prompts = filePrompts[file.file_id] || [];
              const promptsLoading = loadingPrompts[file.file_id];
              const selectedPromptId = filePromptMapping[file.file_id];

              return (
                <Paper
                  key={file.file_id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                    border: selected ? '2px solid' : '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    transition: 'all 0.2s'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {/* 파일 선택 체크박스 */}
                    <Checkbox
                      checked={selected}
                      onChange={(e) => handleToggleFile(file.file_id, e.target.checked)}
                      disabled={disabled || loading}
                      sx={{ mt: -0.5 }}
                    />

                    {/* 파일 정보 및 프롬프트 선택 */}
                    <Box sx={{ flex: 1 }}>
                      {/* 파일명 */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          ID: {file.file_id}
                        </Typography>
                      </Box>

                      {/* 프롬프트 선택 (파일이 선택된 경우에만 표시) */}
                      {selected && (
                        <FormControl fullWidth size="small">
                          {promptsLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="caption" color="text.secondary">
                                프롬프트 로딩 중...
                              </Typography>
                            </Box>
                          ) : prompts.length > 0 ? (
                            <>
                              <Select
                                multiple
                                value={selectedPromptId || []}
                                onChange={(e) => handlePromptChange(file.file_id, e.target.value)}
                                disabled={disabled}
                                displayEmpty
                                renderValue={(selected) => {
                                  if (selected.length === 0) {
                                    return <em style={{ color: '#999' }}>프롬프트 선택 (미선택 시 모두 실행)</em>;
                                  }
                                  return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {selected.map((value) => {
                                        const prompt = prompts.find(p => p.prompt_id === value);
                                        return (
                                          <Chip
                                            key={value}
                                            label={`${prompt?.prompt_name || value} (${value})`}
                                            size="small"
                                            sx={{ fontSize: '0.7rem', height: '22px' }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  );
                                }}
                              >
                                {prompts.map(prompt => (
                                  <MenuItem key={prompt.prompt_id} value={prompt.prompt_id}>
                                    <Box>
                                      <Typography variant="body2">{prompt.prompt_name}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        ID: {prompt.prompt_id}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {prompts.length}개 프롬프트 사용 가능 (복수 선택 가능, Ctrl/Cmd+클릭)
                              </Typography>
                            </>
                          ) : (
                            <Alert severity="warning" sx={{ py: 0.5 }}>
                              이 파일에 연결된 프롬프트가 없습니다
                            </Alert>
                          )}
                        </FormControl>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* 요약 정보 */}
      {selectedFilesCount > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2">
              {selectedFilesCount}개 파일 선택됨 (전체 {files.length}개 중)
            </Typography>
            {selectedFilesCount > 0 && (
              <Chip
                label={`${Object.values(filePromptMapping).filter(v => v && v.length > 0).length}/${selectedFilesCount} 파일에 프롬프트 설정됨`}
                size="small"
                color={Object.values(filePromptMapping).every(v => v && v.length > 0) ? 'success' : 'warning'}
              />
            )}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default FileSelectorWithPrompts;
